// src/components/orders/ShipmentDetailsPopup.tsx — RIGHT SIDE PANEL
import React, { useEffect, useState } from "react";
import { X, MapPin, Anchor, Ship, FileText, Truck, Package, Plane, AlertTriangle } from "lucide-react";

/* ─── Design tokens ──────────────────────────────────────────── */
const FONT         = "'Inter', sans-serif";
const TEXT_PRIMARY = "#111111";
const TEXT_MUTED   = "#6B7280";
const BORDER_COLOR = "#EAEAEA";
const BG_GREY      = "#F7F7F7";
const WL_RED       = "#EF2D3E";
const GREEN        = "#22C55E";

/* ─── Status badge styles ────────────────────────────────────── */
const STATUS_STYLE: Record<string, { label: string; color: string; bg: string; border: string }> = {
  NOT_STARTED: { label: "pending",     color: "#2563EB", bg: "#EFF6FF", border: "#BFDBFE" },
  IN_PROGRESS: { label: "in progress", color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE" },
  AT_RISK:     { label: "at risk",     color: "#EA580C", bg: "#FFF7ED", border: "#FED7AA" },
  DELAYED:     { label: "delayed",     color: WL_RED,    bg: "#FEF2F2", border: "#FECACA" },
  COMPLETED:   { label: "completed",   color: "#16A34A", bg: "#F0FDF4", border: "#BBF7D0" },
};

/* ─── Journey steps ──────────────────────────────────────────── */
interface JourneyStep {
  key: string;
  labelFn: (src?: string, dst?: string) => string;
  sub: string;
  Icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
}

const JOURNEY_STEPS: JourneyStep[] = [
  { key: "origin",    labelFn: (src = "—")                => `Origin: ${src}`,              sub: "Shipment picked up",   Icon: MapPin   },
  { key: "departed",  labelFn: ()                          => "Departed Origin Port",        sub: "Left port facility",   Icon: Anchor   },
  { key: "transit",   labelFn: ()                          => "In Transit",                  sub: "Cargo in transit",     Icon: Ship     },
  { key: "customs",   labelFn: (_src?: string, dst = "—")  => `Customs Clearance – ${dst}`,  sub: "Awaiting processing",  Icon: FileText },
  { key: "delivery",  labelFn: ()                          => "Out for Delivery",            sub: "Final mile delivery",  Icon: Truck    },
  { key: "delivered", labelFn: ()                          => "Delivered",                  sub: "Shipment complete",    Icon: Package  },
];

/* ─── Helpers ─────────────────────────────────────────────────── */
const modeIcon = (mode: string) => {
  const m = (mode || "").toUpperCase();
  if (m.includes("AIR"))   return <Plane size={16} style={{ color: "#2563EB" }} />;
  if (m.includes("TRUCK")) return <Truck size={16} style={{ color: "#EA580C" }} />;
  if (m.includes("RAIL"))  return <Truck size={16} style={{ color: "#7C3AED" }} />;
  return <Ship size={16} style={{ color: "#2563EB" }} />;
};

const toSentenceCase = (v: string): string => {
  const s = String(v).replaceAll("_", " ").trim().toLowerCase();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
};

const toTitleCase = (v: string): string =>
  String(v)
    .replaceAll("_", " ")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");

const fmtCurrency = (v: any): string => {
  const n = Number(v);
  if (!v || Number.isNaN(n) || n === 0) return "—";
  return "$" + Math.round(n).toLocaleString("en-US");
};

/* ─── Types ───────────────────────────────────────────────────── */
interface Order {
  order_id: string;
  destination: string;
  product: string;
  status: string;
  delay_days: string;
  notes: string;
  [key: string]: any;
}

interface ShipmentDetailsPopupProps {
  open: boolean;
  onClose: () => void;
  order: Order | null;
}

/* ─── Component ───────────────────────────────────────────────── */
const ShipmentDetailsPopup: React.FC<ShipmentDetailsPopupProps> = ({ open, onClose, order }) => {
  /* Cache last order so content persists during the slide-out animation */
  const [cachedOrder, setCachedOrder] = useState<Order | null>(null);
  useEffect(() => { if (order) setCachedOrder(order); }, [order]);

  /* Lock body scroll while panel is open */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  /* Escape */
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape" && open) onClose(); };
    globalThis.addEventListener("keydown", handler);
    return () => globalThis.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const o = cachedOrder;
  if (!o) return null;

  /* ── Extract fields ── */
  const shipmentId    = o.shipment_id  || o.order_id  || "—";
  const srcLoc        = o.source_location || "—";
  const dstLoc        = o.destination_location || o.destination || "—";

  const rawStatus     = (o.display_status || o.current_ui_group || o.status || "")
                          .toString().toUpperCase().trim().replaceAll(/[\s-]+/g, "_");
  const statusStyle   = STATUS_STYLE[rawStatus] ?? STATUS_STYLE["IN_PROGRESS"];

  const carrierName   = o.carrier_name   || "—";
  const mode          = toSentenceCase(o.mode || o.transport_mode || "Ocean");
  const totalCost     = fmtCurrency(o.order_value_usd || o.total_cost);
  const tariffImpact  = fmtCurrency(o.tariff_impact   || o.tariff_impact_usd || o.tariff_cost);

  // Derive journey step progress from actual progress_pct, falling back to status-based estimate
  const PROGRESS_FALLBACK: Record<string, number> = {
    COMPLETED: 100, IN_PROGRESS: 55, AT_RISK: 35, DELAYED: 35, BLOCKED: 15, NOT_STARTED: 0,
  };
  const progressPct =
    o.progress_pct != null && o.progress_pct !== ""
      ? Math.min(100, Math.max(0, Number(o.progress_pct)))
      : (PROGRESS_FALLBACK[rawStatus] ?? 0);
  // stepsDone: how many of the 6 journey steps are fully completed
  const stepsDone = Math.round((progressPct / 100) * JOURNEY_STEPS.length);

  // Detail grid fields (from old popup)
  const productName   = o.product_name  || o.product  || "—";
  const supplierName  = o.supplier_name || "—";
  const region        = o.region && !["null","nan",""].includes(String(o.region).toLowerCase()) ? toTitleCase(String(o.region)) : null;
  const orderValue    = fmtCurrency(o.order_value_usd);
  const orderQty      = o.order_quantity   != null ? String(o.order_quantity) : "—";
  const totalWeight   = o.total_weight_kg  != null ? `${o.total_weight_kg} kg` : "—";
  const customerId    = o.customer_id || "—";
  const delayReason   = o.delay_reason && !["null","nan",""].includes(String(o.delay_reason).toLowerCase()) ? String(o.delay_reason) : null;

  const lastUpdated   = (() => {
    if (!o.last_event_ts) return null;
    const d = new Date(o.last_event_ts);
    return Number.isNaN(d.getTime()) ? String(o.last_event_ts)
      : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  })();

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.28)",
          zIndex: 1299,
          opacity: open ? 1 : 0,
          transition: "opacity 0.22s ease",
          pointerEvents: open ? "auto" : "none",
        }}
      />

      {/* ── Right-side Panel ── */}
      <div
        aria-label="Order Intelligence Panel"
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          bottom: 0,
          width: 480,
          maxWidth: "100vw",
          background: "#fff",
          boxShadow: "-4px 0 40px rgba(0,0,0,0.12)",
          zIndex: 1300,
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT,
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          borderBottom: `1px solid ${BORDER_COLOR}`,
          background: "#fff",
          flexShrink: 0,
        }}>
          <span style={{ fontSize: "1.25rem", fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>
            Order Intelligence Panel
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            style={{
              background: "none", border: "none", cursor: "pointer", padding: 6,
              color: TEXT_MUTED, display: "flex", alignItems: "center",
              borderRadius: 6, lineHeight: 1, transition: "background 0.12s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = BG_GREY; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable content ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 24px 8px" }}>

          {/* Order ID + Status badge */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
            <h2 style={{
              margin: 0, fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY,
              letterSpacing: "0", fontFamily: FONT, lineHeight: 1.3,
            }}>
              {shipmentId}
            </h2>
            <span style={{
              flexShrink: 0, marginTop: 2, padding: "4px 14px", borderRadius: 20,
              fontSize: "0.8125rem", fontWeight: 600, fontFamily: FONT,
              background: statusStyle.bg, color: statusStyle.color,
              border: `1px solid ${statusStyle.border}`, whiteSpace: "nowrap",
            }}>
              {o.display_status && String(o.display_status).trim() && !["null","nan"].includes(String(o.display_status).toLowerCase())
                ? String(o.display_status)
                : statusStyle.label}
            </span>
          </div>

          {/* Route subtitle */}
          <p style={{ margin: "0 0 2px", fontSize: "0.8125rem", color: TEXT_MUTED, fontFamily: FONT }}>
            {srcLoc} → {dstLoc}
          </p>
          {lastUpdated && (
            <p style={{ margin: "0 0 20px", fontSize: "0.8125rem", color: TEXT_MUTED, fontFamily: FONT }}>
              Updated {lastUpdated}
            </p>
          )}
          {!lastUpdated && <div style={{ marginBottom: 20 }} />}

          {/* ── Route info card ── */}
          <div style={{ background: BG_GREY, borderRadius: 12, padding: "16px 20px", marginBottom: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
              <div>
                <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 2, fontFamily: FONT }}>Product</div>
                <div style={{ fontSize: "0.8125rem", fontWeight: 400, color: TEXT_MUTED, fontFamily: FONT }}>{productName}</div>
              </div>
              <div>
                <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 2, fontFamily: FONT }}>Carrier</div>
                <div style={{ fontSize: "0.8125rem", fontWeight: 400, color: TEXT_MUTED, fontFamily: FONT }}>{carrierName}</div>
              </div>
              <div>
                <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 2, fontFamily: FONT }}>Transport Mode</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {modeIcon(mode)}
                  <span style={{ fontSize: "0.8125rem", fontWeight: 400, color: TEXT_MUTED, fontFamily: FONT }}>{mode}</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 2, fontFamily: FONT }}>Supplier</div>
                <div style={{ fontSize: "0.8125rem", fontWeight: 400, color: TEXT_MUTED, fontFamily: FONT }}>{supplierName}</div>
              </div>
            </div>
          </div>

          {/* ── Details Grid ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px 20px", marginBottom: 20 }}>
            {[
              { label: "Product",         value: productName  },
              { label: "Supplier",        value: supplierName },
              { label: "Carrier",         value: carrierName  },
              { label: "Order Value",     value: orderValue   },
              { label: "Order Quantity",  value: orderQty     },
              { label: "Total Weight",    value: totalWeight  },
              { label: "Source Location", value: srcLoc       },
              { label: "Destination",     value: dstLoc       },
              { label: "Customer ID",     value: customerId   },
              ...(region ? [{ label: "Region", value: region }] : []),
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 2, fontFamily: FONT }}>{label}</div>
                <div style={{ fontSize: "0.8125rem", fontWeight: 400, color: TEXT_MUTED, fontFamily: FONT }}>{value}</div>
              </div>
            ))}
          </div>

          {/* ── Delay / Risk Reason ── */}
          {(rawStatus === "AT_RISK" || rawStatus === "DELAYED" || delayReason) && (
            <div style={{ marginBottom: 20, padding: "12px 16px", borderRadius: 10, background: "#FFFBEB", border: "1px solid #FCD34D" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <AlertTriangle size={14} style={{ color: "#B45309", flexShrink: 0 }} />
                <span style={{ fontSize: "0.6875rem", fontWeight: 700, color: "#B45309", textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: FONT }}>
                  {rawStatus === "AT_RISK" ? "Risk Reason" : "Delay Reason"}
                </span>
              </div>
              <div style={{ fontSize: "0.875rem", color: "#92400E", fontWeight: 500, fontFamily: FONT }}>
                {delayReason ?? "—"}
              </div>
            </div>
          )}

          {/* ── Order Journey ── */}
          <h3 style={{ margin: "0 0 20px", fontSize: "1rem", fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>
            Order Journey
          </h3>

          <div style={{ marginBottom: 24 }}>
            {JOURNEY_STEPS.map(({ key, labelFn, sub, Icon }, idx) => {
              const done  = idx < stepsDone;
              const last  = idx === JOURNEY_STEPS.length - 1;
              const label = labelFn(srcLoc, dstLoc);
              return (
                <div key={key} style={{ display: "flex", gap: 14 }}>
                  {/* Icon circle + vertical connector */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 40 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: "50%",
                      background: done ? GREEN : "#F1F5F9",
                      border: `2px solid ${done ? GREEN : "#CBD5E1"}`,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <Icon size={18} style={{ color: done ? "#fff" : "#94A3B8" }} />
                    </div>
                    {!last && (
                      <div style={{
                        width: 2, flexGrow: 1, minHeight: 24,
                        background: done ? GREEN : "#CBD5E1", margin: "2px 0",
                      }} />
                    )}
                  </div>
                  {/* Step text */}
                  <div style={{ paddingTop: 8, paddingBottom: last ? 0 : 20, flex: 1 }}>
                    <div style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 2, fontFamily: FONT }}>
                      {label}
                    </div>
                    <div style={{ fontSize: "0.8125rem", color: TEXT_MUTED, fontFamily: FONT }}>{sub}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Cost Breakdown ── */}
          <div style={{ borderTop: `1px solid ${BORDER_COLOR}`, paddingTop: 20, marginBottom: 8 }}>
            <h3 style={{ margin: "0 0 16px", fontSize: "1rem", fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>
              Cost Breakdown
            </h3>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>Total Cost</span>
              <span style={{ fontSize: "0.8125rem", fontWeight: 400, color: TEXT_MUTED, fontFamily: FONT }}>{totalCost}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "0.9375rem", fontWeight: 700, color: TEXT_PRIMARY, fontFamily: FONT }}>Tariff Impact</span>
              <span style={{ fontSize: "0.8125rem", fontWeight: 400, color: WL_RED, fontFamily: FONT }}>{tariffImpact}</span>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default ShipmentDetailsPopup;
